import { UseMutationOptions, useQueryClient } from '@tanstack/react-query';

import useCreateCustomerContact from '@/api/mutations/customers/useCreateCustomerContact';
import customerKeys from '@/api/queries/customers/keyFactory';
import {
  CreateCustomerContactMutation as _CreateCustomerContactMutation,
  CreateCustomerContactInput,
  CustomerQuery,
} from '@/gql/graphql';

const useOptimisticCreateCustomerContactOnCustomer = (
  customerId: string,
  options?: UseMutationOptions<
    _CreateCustomerContactMutation,
    unknown,
    CreateCustomerContactInput
  >
) => {
  const queryClient = useQueryClient();

  return useCreateCustomerContact({
    ...options,
    onMutate: async (variables) => {
      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({
        queryKey: customerKeys.customer(customerId),
      });

      // Snapshot the previous value
      const previousCustomer = queryClient.getQueryData(
        customerKeys.customer(customerId)
      );

      // Optimistically update to the new value
      queryClient.setQueryData<CustomerQuery>(
        customerKeys.customer(customerId),
        (old) => {
          if (!old) return;

          return {
            ...old,
            customer: {
              ...old.customer,
              contacts: [
                ...old.customer.contacts,
                {
                  ...variables,
                  id: 'temp-id',
                  publicId: 'temp-public-id',
                  isPrimary: false,
                },
              ],
            },
          };
        }
      );

      options?.onMutate?.(variables);

      // Return a context object with the snapshotted value
      return { previousCustomer };
    },
    onError: (err, newTodo, context) => {
      // If an error happens, rollback!
      queryClient.setQueryData(
        customerKeys.customer(customerId),
        // @ts-expect-error
        context.previousCustomer
      );

      options?.onError?.(err, newTodo, context);
    },
    // Always refetch after error or success:
    onSettled: (...args) => {
      void queryClient.invalidateQueries({
        queryKey: customerKeys.customer(customerId),
      });

      options?.onSettled?.(...args);
    },
  });
};

export default useOptimisticCreateCustomerContactOnCustomer;
